import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { navigation, footer } from '../data/mock';
import MitharaLogo from './MitharaLogo';

const NAV_SCROLL_THRESHOLD_PX = 80;
const LOGO_HEIGHT_NAV = 32;
const LOGO_HEIGHT_FOOTER = 30;
const MOBILE_ICON_SIZE = 22;
const CLOSE_ICON_SIZE = 24;
const ROW_LINK_ICON_SIZE = 12;

/* --- useInView hook --- */
export const useInView = (opts = {}) => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  const threshold = opts.threshold ?? 0.1;
  const rootMargin = opts.rootMargin ?? '0px 0px -30px 0px';
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setV(true); obs.unobserve(e.target); } },
      { threshold, rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);
  return [ref, v];
};

/* --- AnimateIn --- */
export const AnimateIn = ({ children, className = '', delay = 0, animation = 'fade-up' }) => {
  const [ref, vis] = useInView();
  return (
    <div ref={ref} data-anim={animation}
      className={`${className} ${vis ? 'anim-visible' : 'anim-hidden'}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

/* --- Section Row Divider --- */
export const SectionRow = ({ number, label, linkText, linkHref, dark }) => (
  <div className={`section-row ${dark ? 'on-dark' : ''}`}>
    <span className="row-number">{number}</span>
    <span className="row-label">• {label}</span>
    {linkText ? (
      <Link to={linkHref || '#'} className="row-link">
        {linkText} <ArrowUpRight size={ROW_LINK_ICON_SIZE} style={{ display: 'inline' }} />
      </Link>
    ) : <span />}
  </div>
);

/* --- Navigation --- */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location]);

  return (
    <>
      <nav className={`nav-main ${scrolled ? 'nav-scrolled' : ''}`}>
        <Link to="/" className="nav-logo" aria-label="Mithara Institute home">
          <MitharaLogo height={LOGO_HEIGHT_NAV} color="#F3F3F1" />
        </Link>
        <div className="nav-center">
          {navigation.items.map(i => (
            <NavLink key={i.href} to={i.href}
              className={({ isActive }) => isActive ? 'active' : ''}>
              {i.label}
            </NavLink>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/research" className="nav-cta-btn">• Read the Paper</Link>
          <button className="mobile-toggle" onClick={() => setMobileOpen(true)} aria-label="Menu">
            <Menu size={22} />
          </button>
        </div>
      </nav>
      <div className={`mobile-overlay ${mobileOpen ? 'open' : ''}`}>
        <button className="close-mobile" onClick={() => setMobileOpen(false)} aria-label="Close">
          <X size={24} />
        </button>
        {navigation.items.map(i => (
          <Link key={i.href} to={i.href} onClick={() => setMobileOpen(false)}>{i.label}</Link>
        ))}
      </div>
    </>
  );
};

/* --- Footer --- */
const Footer = () => (
  <footer className="footer-main">
    <div className="footer-top">
      <div className="footer-brand">{footer.brand}</div>
      {footer.sections.map(s => (
        <div key={s.num}>
          <div className="footer-col-label">{s.num}</div>
          <div className="footer-col-title">{s.label}</div>
          {s.links.map(l => (
            l.href.startsWith('/') ?
              <Link key={l.label} to={l.href} className="footer-col-link">{l.label}</Link> :
              <a key={l.label} href={l.href} className="footer-col-link" target="_blank" rel="noopener noreferrer">{l.label}</a>
          ))}
        </div>
      ))}
    </div>
    <div className="footer-bottom">
      <MitharaLogo height={LOGO_HEIGHT_FOOTER} color="rgba(243,243,241,0.3)" />
      <div className="footer-legal">
        {footer.legal.map(l => <a key={l.label} href={l.href}>{l.label}</a>)}
      </div>
      <span className="footer-loc">{footer.location}</span>
    </div>
  </footer>
);

/* --- Layout --- */
const Layout = ({ children }) => (
  <>
    <Navigation />
    <main>{children}</main>
    <Footer />
  </>
);

export default Layout;
